import { initializeApp } from '../libs/init/exec.init';

await initializeApp();

import { useEffect, type ReactElement, type ReactNode } from 'react';
import Head from 'next/head';
import { Provider } from 'react-redux';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@doxyme/ui/theme';
import { ScrollbarStyles } from '@doxyme/ui/components/misc';
import type { AppProps } from 'next/app';
import { IntlProvider } from '@doxyme/locale';
import { SingletonHooksContainer } from 'react-singleton-hook';
import { RegistryProvider } from '@doxyme/extensions-bridge-react';
import { store } from '../stores/redux';
import { GlobalStyles } from '../shared/styles';
import {
  ExtensionsErrorBoundary,
  LoggerErrorErrorBoundary,
} from '../components/global/errorBoundary';
import { Toaster } from '../components/global/toaster';
import { ExtensionsMain } from '../components/extensions';
import { usePageVisibilityBeacon } from '../hooks/usePageVisibilityBeacon';
import { type NextPage } from 'next';
import { ServiceContainerProvider } from '../context';
import { serviceContainer } from '../container';
import { useHideInjectedContent } from '../hooks/useHideInjectedContent';
import { useStatuspageIncidentEmbedSuppression } from '../hooks/useStatuspageIncidentEmbedSuppression/useStatuspageIncidentEmbedSuppression';
import { useCloseModalOnRouteChange } from '../hooks/useCloseModal';
import { useAppRouter } from '../hooks/useAppRouter';
import { getPublicRuntimeConfig } from '../libs/init/getPublicRuntimeConfig';
import { AppMain } from '../components/global/appMain';
import { RestoreModalFocusMount } from '../components/global/restoreModalFocusMount';
import { useFixStuckZoom } from '../hooks/useFixStuckZoom';

import { useOnlineStatusBeacon } from '../hooks/useOnlineStatusBeacon';
import { registerVersioningServiceWorker } from '../utils/registerVersioningServiceWorker';
import { HotpotProviderWrapper } from '../components/hotpot/HotpotProviderWrapper';
import { lazyWithRetry } from '../utils/lazyWithRetry';

const FronteggAppProviderWrapperDynamic = lazyWithRetry(
  () => import('../components/auth/frontegg/frontegg.provider'),
  'FronteggAppProviderWrapper',
);

export type NextPageWithLayout<P = unknown, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

// isIframe --> We want to load a basic HTML page. Can be used for pages which are iFrames that need no other context
type AppPropsWithLayout = AppProps<{ isIframe?: boolean }> & {
  Component: NextPageWithLayout;
};

function CustomApp(props: AppPropsWithLayout): ReactElement {
  const { Component } = props;
  const { pathname } = useAppRouter();
  const { paths } = getPublicRuntimeConfig();

  const isInCall = pathname === paths.call;

  useEffect(() => {
    registerVersioningServiceWorker();
  }, []);

  return Boolean(props.pageProps.isIframe) ? (
    <>
      <Head>
        <meta name='viewport' content='width=device-width, initial-scale=1' />
      </Head>
      {/** Only using theme provider to enable the global size styles for body / html / next tags.
       We don't care much for the theme itself */}
      <ThemeProvider themeName='light'>
        <GlobalStyles />
        <ScrollbarStyles isInCall={isInCall} />
        <main
          className='app'
          style={{ height: 'inherit', width: 'inherit', overflow: 'hidden' }}
        >
          <Component {...props.pageProps} />
        </main>
      </ThemeProvider>
    </>
  ) : (
    <>
      <Head>
        <meta name='viewport' content='width=device-width, initial-scale=1' />
      </Head>
      <ServiceContainerProvider container={serviceContainer}>
        <IntlProvider>
          <ThemeProvider themeName='light'>
            <FronteggAppProviderWrapperDynamic>
              <Provider store={store}>
                <RegistryProvider>
                  <HotpotProviderWrapper>
                    <SingletonHooksContainer />
                    <Toaster />
                    <RestoreModalFocusMount />
                    <CustomAppWithProviders {...props} />
                    <ExtensionsErrorBoundary>
                      <ExtensionsMain />
                    </ExtensionsErrorBoundary>
                  </HotpotProviderWrapper>
                </RegistryProvider>
              </Provider>
            </FronteggAppProviderWrapperDynamic>
          </ThemeProvider>
        </IntlProvider>
      </ServiceContainerProvider>
    </>
  );
}

function CustomAppWithProviders({
  Component,
  pageProps,
}: AppPropsWithLayout): ReactElement {
  usePageVisibilityBeacon();
  useOnlineStatusBeacon();

  const { pathname } = useAppRouter();
  const { paths } = getPublicRuntimeConfig();

  const isInCall = pathname === paths.call;

  useHideInjectedContent();

  useStatuspageIncidentEmbedSuppression();

  useCloseModalOnRouteChange();

  useFixStuckZoom();

  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <>
      <CssBaseline />
      <GlobalStyles />
      <ScrollbarStyles isInCall={isInCall} />
      <AppMain>
        <LoggerErrorErrorBoundary>
          {getLayout(<Component {...pageProps} />)}
        </LoggerErrorErrorBoundary>
      </AppMain>
    </>
  );
}

export default CustomApp;
